'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useTheme } from 'next-themes';
import type { ReactNode } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Moon02Icon, Sun03Icon } from '@/components/ui/icon/glyphs';
import { setThemeWithTransition } from '@/lib/ui/theme-transition';

/**
 * Dark/light toggle for the rewards header — reuses the sidebar's theme
 * controls (`useTheme` + `setThemeWithTransition`) so both stay in lockstep
 * (MOR-773). The visible glyph is driven by the `dark:` class variant rather
 * than a mount-gated `useState`, so it matches the rendered theme with no
 * hydration flash and stays React-Compiler friendly.
 */
export function RewardsThemeToggle() {
  const $ = _c(10);
  const t = useTranslations("rewards");
  const {
    resolvedTheme,
    setTheme
  } = useTheme();
  let t0;
  if ($[0] !== t) {
    t0 = t("userMenu.toggleTheme");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== resolvedTheme || $[3] !== setTheme) {
    t1 = () => {
      setThemeWithTransition(setTheme, resolvedTheme === "dark" ? "light" : "dark");
    };
    $[2] = resolvedTheme;
    $[3] = setTheme;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Icon icon={Moon02Icon} className="size-4 dark:hidden" />;
    t3 = <Icon icon={Sun03Icon} className="hidden size-4 dark:block" />;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t0 || $[8] !== t1) {
    t4 = <AnimatedButton variant="secondary" size="sm" className="size-8 px-0" data-testid="rewards-theme-toggle" aria-label={t0} onClick={t1}>{t2}{t3}</AnimatedButton>;
    $[7] = t0;
    $[8] = t1;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}